<template>
  <Transition name="video-toast">
    <div v-if="visible" class="video-toast" :class="`video-toast--${toast.kind}`" role="status">
      {{ text }}
    </div>
  </Transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatAmount } from '@/utils/helpers';

// How long the pill stays (the prototype's toast spec: it hides itself after 1.8s).
const VISIBLE_MS = 1800;

/*
 * The pill over the centre of a table's video: "Bet placed $30.00" when a bet is
 * confirmed, "Payout $89.10" on a win (it replaces the pink PAYOUT ribbon), and
 * "Unconfirmed bets cancelled" when betting closes on bets that were never
 * confirmed. No toast on a loss. The caller passes the toast -- Tài Xỉu's
 * `taiXiu/videoToast` for its table, cockfight's `bets/videoToast` (placed and
 * cancelled only) -- and a `source` (table / arena) that hides it on a switch.
 * A new toast (a new `key`) restarts the timer.
 */
export default {
  name: 'VideoToast',
  props: {
    // { key, kind: 'placed' | 'payout' | 'cancelled', amount }
    toast: {
      type: Object,
      default: null,
    },
    source: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      visible: false,
      timer: null,
    };
  },
  computed: {
    ...mapGetters({
      member: 'general/member',
    }),
    text() {
      if (!this.toast) return '';
      if (this.toast.kind === 'cancelled') return this.$t('Unconfirmed bets cancelled');
      const label = this.toast.kind === 'payout' ? this.$t('Payout') : this.$t('Bet placed');
      const symbol = (this.member && this.member.currencySymbol) || '';
      return `${label} ${symbol}${formatAmount(this.toast.amount)}`;
    },
  },
  watch: {
    // Only a toast raised while this is mounted is shown: one left over from
    // before (another pane, an earlier visit) is not replayed.
    'toast.key'(key, previous) {
      if (!key || key === previous) return;
      this.visible = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.visible = false;
      }, VISIBLE_MS);
    },
    source() {
      clearTimeout(this.timer);
      this.visible = false;
    },
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
};
</script>

<style lang="scss" scoped>
.video-toast {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 5;
  max-width: 86%;
  padding: 10px 22px;
  border-radius: 999px;
  transform: translate(-50%, -50%);
  background: #14213a;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;

  &--payout {
    background: #1f9a56;
  }
}

.video-toast-enter-active,
.video-toast-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.video-toast-enter,
.video-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, -42%);
}
</style>
